(function () {
  if (customElements.get('price-features-sticky')) return;

  class PriceFeaturesSticky extends HTMLElement {
    constructor() {
      super();
      this.schedule = this.schedule.bind(this);
    }

    static get observedAttributes() {
      return ['data-header-mode'];
    }

    connectedCallback() {
      // Defer until Shopify has inserted this instance's child blocks.
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.host) return;
      this.host = this.querySelector(':scope > .price-features_products-block');
      if (!this.host) return;
      this.coordinator = window.PlaudStickyHeaderCoordinator;
      if (!this.coordinator || !window.ResizeObserver || !window.IntersectionObserver) {
        this.host = null;
        return;
      }

      this.inView = true;
      this.unsubscribe = this.coordinator.subscribe(this.schedule);
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.host);
      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      });
      this.visibilityObserver.observe(this);
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule);
      window.visualViewport?.addEventListener('resize', this.schedule);
      this.update();
    }

    attributeChangedCallback() {
      if (this.host) this.schedule();
    }

    schedule() {
      if (!this.isConnected || this.pendingFrame) return;
      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = 0;
        this.update();
      });
    }

    update() {
      if (!this.host) return;
      if (!this.inView) {
        this.removeAttribute('data-price-features-pinned');
        this.coordinator.release(this);
        return;
      }

      const bounds = this.getBoundingClientRect();
      const height = this.host.getBoundingClientRect().height;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const replace = this.dataset.headerMode !== 'stack';
      // Replacement mode always gives the pinned price bar priority over navigation.
      const offset = replace ? 0 : this.coordinator.getOffset();
      // Leave at least half the viewport available for the table, even in landscape.
      const enabled = height > 0 && height + offset <= viewportHeight / 2 && bounds.height > height;
      this.host.toggleAttribute('data-sticky-enabled', enabled);
      const top = offset + 'px';
      if (this.host.style.getPropertyValue('--price-features-sticky-top') !== top) {
        this.host.style.setProperty('--price-features-sticky-top', top);
      }

      // The layout's top never moves when the price block becomes sticky.
      // No cached document coordinates or placeholder heights are needed.
      const pinned = enabled && bounds.top < offset && bounds.bottom > offset + height;
      this.toggleAttribute('data-price-features-pinned', pinned);
      this.coordinator.request(this, replace && pinned);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = 0;
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      window.visualViewport?.removeEventListener('resize', this.schedule);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.coordinator?.release(this);
      this.unsubscribe?.();
      this.host?.removeAttribute('data-sticky-enabled');
      this.host?.style.removeProperty('--price-features-sticky-top');
      this.removeAttribute('data-price-features-pinned');
      this.host = null;
    }
  }

  customElements.define('price-features-sticky', PriceFeaturesSticky);
})();
